import React, { useCallback, useEffect, useMemo, useState } from 'react';

import i18n from 'i18n-react';
import { Redirect } from 'react-router-dom';
import { cssTransition, toast, ToastContainer } from 'react-toastify';

import BackImage from 'components/BackImage';
import BookingForm from 'components/BookingForm';
import type { BookingFormSchema } from 'components/BookingForm/types';
import { ContentHeader } from 'components/ContentHeader/ContentHeader';
import RewardMenu from 'components/RewardMenu';

import APIHandler from 'config/APIHandler';
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

import { isRewardsCountry } from 'helpers/isRewardsCountry';
import { legacyRemCss } from 'helpers/legacyRem';

import 'react-toastify/dist/ReactToastify.css';

interface Agent {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  id?: string;
}

interface storedUser {
  email: string;
  first_name?: string;
  last_name?: string;
  agency_name?: string;
  zip_code?: string;
  city?: string;
  country?: string;
  id_number?: string;
}

type FormData = Record<string, string>;

interface RewardResponse {
  result: 'success' | 'failed' | 'error';
  response?: string;
  message?: string;
  errors?: unknown;
}

interface BookingFormSchemaResponse {
  apps: Record<string, BookingFormSchema>;
}

const calculateLengthOfStayInNights = (arrivalDate?: string, departureDate?: string) => {
  if (!arrivalDate || !departureDate) return 0;

  const arrival = new Date(arrivalDate);
  const departure = new Date(departureDate);

  const diffTime = Math.abs(departure.getTime() - arrival.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const Zoom = cssTransition({
  enter: 'zoomIn',
  exit: 'zoomOut',
  duration: [500, 800],
});

// Loads the schema from Amazon S3 or local file
async function loadBookingFormSchema(appId: string): Promise<BookingFormSchemaResponse | null> {
  try {
    // Check if local schema should be used (for testing)
    const useLocalSchema = import.meta.env.VITE_USE_LOCAL_BOOKING_SCHEMA === 'true';

    const url = useLocalSchema
      ? '/bookingFormView.schema.json'
      : `https://front.travpromobile.com/${appId}/bookingFormView.schema.json`;

    const response = await fetch(url, { cache: 'no-store' });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const schema = (await response.json()) as BookingFormSchemaResponse;

    if (useLocalSchema) {
      console.info('📋 Using local booking form schema from:', url);
    }

    return schema;
  } catch (error) {
    console.error('Failed to load booking form schema:', error);
    return null;
  }
}

const BookingFormView = () => {
  const [formData, setFormData] = useState<FormData>({});
  const [agent, setAgent] = useState<Agent>({ email: '' });
  const [files, setFiles] = useState<string>('');
  const [redirect, setRedirect] = useState<string | null>(null);
  const [schema, setSchema] = useState<BookingFormSchema | null>(null);
  const [logo, setLogo] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsUrl, setTermsUrl] = useState<string | null>(null);

  const params = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);
  const mobile = params.get('mobile');

  const checkLoggedIn = useCallback(async () => {
    const user = (await StorageHandler.load(storageKeys.user)) as storedUser | null;

    if (!user) {
      window.location.href = `${window.location.origin}/login`;
      return;
    }

    setFormData({});
    setAgent({
      email: user.email,
      firstName: user.first_name,
      lastName: user.last_name,
      company: user.agency_name,
      postalCode: user.zip_code,
      city: user.city,
      country: user.country,
      id: user.id_number,
    });
    setFiles('');
    setSchema(null);
    setLogo(null);
    setRedirect(null);
  }, []);

  useEffect(() => {
    void (async () => {
      await checkLoggedIn();
    })();
  }, [checkLoggedIn]);

  useEffect(() => {
    if (!agent.email) return;

    let cancelled = false;

    void (async () => {
      try {
        await checkLoggedIn();

        const appId = import.meta.env.VITE_APP_ID;
        if (!appId) {
          toast.error('Application configuration is missing.');
          return;
        }

        interface AppInfo {
          featureFlags?: { name: string; is_enabled?: boolean }[];
          logo?: string;
        }

        const appInfoRaw = (await StorageHandler.load(storageKeys.appInfo)) as unknown;

        const appInfo: AppInfo | null = appInfoRaw && typeof appInfoRaw === 'object' ? appInfoRaw : null;

        const rewardsBookingEnabled = !!appInfo?.featureFlags?.some((flag) => {
          return flag.name === 'rewards_booking' && (flag.is_enabled ?? true);
        });

        if (!rewardsBookingEnabled || !(await isRewardsCountry(agent.country))) {
          if (!cancelled) setRedirect('/main/rewards');
          return;
        }

        const rawSchema = await loadBookingFormSchema(appId);

        const resolvedSchema: BookingFormSchema | null = rawSchema?.apps?.[appId] ?? null;

        if (!resolvedSchema?.fields) {
          toast.error('Form configuration is missing.');
          // setRedirect('/main/rewards');
          return;
        }

        if (!cancelled) {
          setSchema(resolvedSchema);
          setLogo(appInfo?.logo ?? null);
        }

        // Fetch contest data to get terms URL
        const lang = (await StorageHandler.load(storageKeys.lang)) as { code?: string; langFile?: string } | null;
        const contests = (await APIHandler.getContests(appId, {
          currentDate: new Date().toISOString(),
          marketFilter: lang?.code ? [{ id: lang.code.toUpperCase() }] : ([] as { id: string }[]),
        })) as { terms_url?: string }[];

        if (!cancelled && Array.isArray(contests) && contests.length > 0 && contests[0]?.terms_url) {
          setTermsUrl(contests[0].terms_url);
        }
      } catch (error) {
        console.error(error);
        toast.error('Failed to load booking form configuration.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [agent.email, agent.country, checkLoggedIn]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  }, []);

  const handleChangeFile = useCallback((fileUrl: string) => {
    setFormData((prev) => {
      return {
        ...prev,
        file: fileUrl,
      };
    });
  }, []);

  const submitForm = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        if (!schema?.fields) {
          toast.error('Form configuration is missing.');
          return;
        }

        const missingRequiredFields = schema.fields
          .filter((field) => {
            return field.required;
          })
          .filter((field) => {
            return !formData[field.name];
          });

        if (missingRequiredFields.length > 0) {
          const missingLabel = i18n.translate('rewards.bookingForm.missingRequiredFields');
          const missingLabelText =
            typeof missingLabel === 'string' ? missingLabel : 'Please fill in all required fields.';

          toast.error(
            `${missingLabelText}: ${missingRequiredFields
              .map((field) => {
                const t = i18n.translate(field.labelKey);
                return typeof t === 'string' ? t : field.labelKey;
              })
              .join(', ')}`,
            {
              position: 'top-center',
              transition: Zoom,
              autoClose: 10000,
              hideProgressBar: true,
              pauseOnHover: true,
              draggable: true,
            },
          );
          return;
        }

        const invalidNumericField = schema.fields
          .filter((field) => {
            return field.type === 'number' || field.type === 'currency';
          })
          .find((field) => {
            const raw = formData[field.name];
            if (!raw) return false;
            const value = Number(raw);
            return Number.isNaN(value) || value <= 0;
          });

        if (invalidNumericField) {
          const translatedLabel = i18n.translate(invalidNumericField.labelKey);
          const label = typeof translatedLabel === 'string' ? translatedLabel : invalidNumericField.labelKey;

          const translatedMinimum = i18n.translate('rewards.bookingForm.invalidNumber');
          const minimumMessage =
            typeof translatedMinimum === 'string' ? translatedMinimum : 'Must be a number greater than 0.';

          toast.error(`${label} ${minimumMessage}`, {
            position: 'top-center',
            transition: Zoom,
            autoClose: 10000,
            hideProgressBar: true,
            pauseOnHover: true,
            draggable: true,
          });

          return;
        }

        const payload: Record<string, unknown> = {};

        schema.fields.forEach((field) => {
          const rawValue = formData[field.name];

          if (!field.api_key || rawValue === undefined || rawValue === null || rawValue === '') return;

          if (field.type === 'currency' || field.type === 'number') {
            const num = Number(rawValue);

            if (Number.isNaN(num)) {
              const t = i18n.translate(field.labelKey);
              const label = typeof t === 'string' ? t : field.labelKey;
              throw new Error(`${label} must be a valid number.`);
            }

            payload[field.api_key] = num;
            return;
          }

          payload[field.api_key] = rawValue;
        });

        payload.agent_email = agent.email;
        payload.agent_first_name = agent.firstName ?? '';
        payload.agent_last_name = agent.lastName ?? '';
        payload.app_id = import.meta.env.VITE_APP_ID;

        payload.length_of_stay = calculateLengthOfStayInNights(formData.arrivalDate, formData.departureDate);

        const res: unknown = await APIHandler.addReward(payload);

        const rewardRes: RewardResponse =
          typeof res === 'object' && res !== null && 'result' in res
            ? (res as RewardResponse)
            : { result: 'error', message: 'Unexpected API response' };

        if (rewardRes.result !== 'success') {
          toast.error(rewardRes.response ?? rewardRes.message ?? 'Something went wrong, please try again.', {
            position: 'top-center',
            transition: Zoom,
            autoClose: 10000,
            hideProgressBar: true,
            pauseOnHover: true,
            draggable: true,
          });
          return;
        }

        const p = new URLSearchParams(window.location.search);
        setRedirect(p.get('mobile') ? '/main/rewards/thank-you?mobile=true' : '/main/rewards/thank-you');
      } finally {
        setIsSubmitting(false);
      }
    },
    [agent.email, agent.firstName, agent.lastName, formData, isSubmitting, schema],
  );

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      void submitForm(event);
    },
    [submitForm],
  );

  return (
    <div className="contentContainer bookingFormView">
      {window.location.pathname.includes('/main') ? <RewardMenu /> : <BackImage darken />}

      <section className="headerFirst">
        <ContentHeader
          header={i18n.translate('rewards.bookingForm.title')}
          // subtitle={i18n.translate('rewards.bookingForm.subTitle') as string}
          pageHeader
        />
      </section>

      <hr />

      <BookingForm
        schema={schema}
        logo={logo ?? undefined}
        submitForm={handleSubmit}
        handleChange={handleChange}
        handleChangeFile={handleChangeFile}
        agentEmail={agent.email}
        agentFirstName={agent.firstName}
        agentLastName={agent.lastName}
        agentCompany={agent.company}
        postalCode={agent.postalCode}
        agentCity={agent.city}
        agentCountry={agent.country}
        agentId={agent.id}
        files={files}
        termsUrl={termsUrl}
        {...formData}
      />

      <ToastContainer draggablePercent={70} />

      {redirect && <Redirect to={redirect} />}

      {mobile && (
        <style>
          {`.contentContainer { margin-top: ${legacyRemCss(5)}; } .header { display: none; } .cookieConsent { display: none; }`}
        </style>
      )}
    </div>
  );
};

export default BookingFormView;
